import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import queryClient from "../configs/react-query/query-client";
import queryKeys from "../configs/react-query/query-key";
import { PROGRESS_CLARITY_STATUS, VERIFICATION_TYPE } from "../constants/index";
import { processComponentConflictFlags } from "../utils/common";
import { useFetchData } from "./useFetchData";
import { useMutationData } from "./useMutationData";
import { FORCE_COMPLETE_VERIFICATION_ACTION } from "@constants/action";

export const useClarityVerification = (
  projectSeq,
  binaryVerificationSeq,
  modifiedComponentData = [],
  binaryScanFollowupResults = [],
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState("ON_ANALYSIS");
  const [approvalFlag, setApprovalFlag] = useState("N");
  const [approvalRequestFlag, setApprovalRequestFlag] = useState("N");
  const [projectData, setProjectData] = useState({});
  const [verificationData, setVerificationData] = useState({});
  const [verificationComment, setVerificationComment] = useState("");
  const [verificationFile, setVerificationFile] = useState(null);
  // Create a ref to access FulfillmentInfo component methods
  const fulfillmentInfoRef = useRef(null);
  const [currentActionData, setCurrentActionData] = useState({});
  const [verificationReportsData, setVerificationReportsData] = useState([]);
  // Add approval comment state
  const [approvalComment, setApprovalComment] = useState("");
  const [fulfillmentComment, setFulfillmentComment] = useState("");
  // queryClient.invalidateQueries({ queryKey: [queryKeys.getClarityById, binaryVerificationSeq] });

  // Fetch project data
  const { data: fetchedProjectData, isLoading: projectDataLoading, isFetching: projectDataFetching } = useFetchData(
    [queryKeys.getProjectByProjectId, projectSeq],
    `/project/${projectSeq}`,
    { method: "GET" },
    {},
    {
      enabled: !!projectSeq,
      refetchOnMount: true, // Force refetch when component mounts
      staleTime: 0,
    },
  );

  // Fetch verification data
  const { data: fetchedVerificationData, error: fetchedVerificationDataError, isLoading: verificationDataLoading, isFetching: verificationDataFetching } = useFetchData(
    [queryKeys.getClarityById, binaryVerificationSeq],
    `/${projectSeq}/binary_verification/${binaryVerificationSeq}`,
    { method: "GET" },
    {},
    {
      enabled: !!binaryVerificationSeq,
      refetchOnMount: true, // Force refetch when component mounts
      staleTime: 0, // Consider data stale immediately
    },
  );

  useEffect(() => {
    if (fetchedVerificationDataError) {
      if (fetchedVerificationDataError?.response?.status === 404) {
        navigate("/404", { state: { backTo: "/projects" } });
      }
    }
  }, [fetchedVerificationDataError]);

  // Fetch binary actions data
  const { data: fetchedBinaryAction } = useFetchData(
    ["binary_action", binaryVerificationSeq],
    "/binary_action",
    {
      method: "GET",
      params: {
        verificationSeq: binaryVerificationSeq,
      },
    },
    {},
    {
      enabled: !!binaryVerificationSeq,
      refetchOnMount: true, // Force refetch when component mounts
      staleTime: 0, // Consider data stale immediately
    },
  );

  // Mutation for updating approval request flag
  const updateApprovalRequestFlagMutation = useMutationData(
    "/binary_verification_approval_request_flag",
    { method: "PUT" },
    {
      onSuccess: (response) => {
        // Update local state after successful API call
        setApprovalRequestFlag(response.approvalRequestFlag);
        navigate(`/projects/${projectSeq}`);
      },
      onError: (error) => {
        console.error("Error updating approval request flag:", error);
      },
    },
  );

  // Mutation for updating approval flag
  const updateApprovalFlagMutation = useMutationData(
    "/binary_verification_approval_flag",
    { method: "PUT" },
    {
      onSuccess: (response) => {
        // Update local state after successful API call
        setApprovalFlag(response.approvalFlag);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
      },
      onError: (error) => {
        console.error("Error updating approval flag:", error);
      },
    },
  );

  const updateVerificationStatusToReviewMutation = useMutationData(
    "/binary_verification_status?preStatus=",
    { method: "PUT" },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_CLARITY_STATUS.REVIEW);

        // Invalidate relevant queries to ensure fresh data
        queryClient.invalidateQueries({
          queryKey: [
            queryKeys.getBinaryScanComponentByProjectSeqAndVerificationVersion,
            projectId,
            verificationVersion,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getClarityById, binaryVerificationSeq],
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getProjectByProjectId, projectSeq],
        });

        // Navigate to project detail after successful API call
        navigate(`/projects/${projectSeq}`);
      },
      onError: (error) => {
        console.error("Error updating verification status:", error);
      },
    },
  );

  // New mutation for rejecting verification requests and going back to verification status
  const rejectVerificationRequestMutation = useMutationData(
    "/binary_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: PROGRESS_CLARITY_STATUS.REVIEW,
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_CLARITY_STATUS.VERIFICATION);
        // Reset the approval request flag
        setApprovalRequestFlag("N");
        navigate(`/projects/${projectSeq}`);
      },
      onError: (error) => {
        console.error("Error rejecting verification request:", error);
      },
    },
  );

  const updateBinaryVerificationToFulfillmentMutation = useMutationData(
    "/binary_verification_status",
    {
      method: "PUT",
      params: { preStatus: PROGRESS_CLARITY_STATUS.REVIEW },
    },
    {
      onSuccess: (response) => {
        setFulfillmentComment(response.followupComment);
        if (verificationFile) {
          const formData = new FormData();
          formData.append("file", verificationFile);
          formData.append("binaryVerificationSeq", binaryVerificationSeq);
          uploadBinaryVerificationFileMutation.mutate(formData);
        }
      },
    },
  );

  // New mutation for decompleting verification to ON_VERIFICATION instead of ON_REVIEW
  const decompleteVerificationMutation = useMutationData(
    "/binary_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: PROGRESS_CLARITY_STATUS.VERIFICATION,
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_CLARITY_STATUS.VERIFICATION);
        // Reset the approval flags
        setApprovalFlag("N");
        setApprovalRequestFlag("N");
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
      },
      onError: (error) => {
        console.error("Error decompleting verification:", error);
      },
    },
  );

  const forceToDoneMutation = useMutationData(
    "/binary_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
        action: FORCE_COMPLETE_VERIFICATION_ACTION
      },
    },
    {
      onSuccess: () => {
        if (verificationFile) {
          const formData = new FormData();
          formData.append("file", verificationFile);
          formData.append("binaryVerificationSeq", binaryVerificationSeq);
          uploadBinaryVerificationFileMutation.mutate(formData);
        }
        setVerificationStatus(PROGRESS_CLARITY_STATUS.DONE);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  const fileGroupNo = fetchedProjectData?.fileGroupNo;
  const licensePolicySeq = fetchedProjectData?.licensePolicySeq;
  const verificationVersion = fetchedVerificationData?.verificationVersion;
  const projectId = fetchedVerificationData?.projectId;

  // Fetch license policy
  const { data: fetchedLicensePolicy, isLoading: licensePolicyLoading, isFetching: licensePolicyFetching } = useFetchData(
    [queryKeys.getLicensePolicyByLicensePolicySeq, licensePolicySeq],
    `/license_policy/${licensePolicySeq}`,
    { method: "GET" },
    {},
    {
      enabled: !!licensePolicySeq,
    },
  );

  const { data: fetchedBinaryScanComponent, isLoading: binaryScanComponentLoading, isFetching: binaryScanComponentFetching } = useFetchData(
    [queryKeys.getBinaryScanComponentByProjectSeqAndVerificationVersion, projectId, verificationVersion],
    "/binary_scan_component",
    {
      method: "GET",
      params: {
        projectId: projectId,
        verificationVersion: verificationVersion,
      },
    },
    {},
    {
      enabled: !!projectId && !!verificationVersion,
    },
  );

  const {
    data: fetchedBinaryScanFollowupPlan,
    dataUpdatedAt: binaryScanFollowupPlanUpdatedAt,
  } = useFetchData(
    ["binary_scan_followup_plan", projectId, verificationVersion],
    "/binary_scan_followup_plan",
    {
      method: "GET",
      params: {
        projectId: projectId,
        verificationVersion: verificationVersion,
      },
    },
    {},
    {
      enabled: !!projectId && !!verificationVersion,
      refetchOnMount: true,
      staleTime: 0,
    },
  );

  const fetchedVerificationReportForPopup = useMutationData(
    "/verification_report_for_popup",
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        if (response && Array.isArray(response)) {
          setVerificationReportsData(response);
        }
      },
      onError: (error) => {
        console.error("Error getting verification report for popup:", error);
      },
    },
  );

  const moveToFulfillmentMutation = useMutationData(
    "/binary_verification_followup_results",
    {
      method: "PUT",
    },
    {
      onSuccess: () => {
        // Success will be handled by the calling function
      },
      onError: (error) => {
        console.error("Error move to fulfillment:", error);
      },
    },
  );

  const updateBinaryActionMutation = useMutationData(
    "/binary_action",
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (response) {
          setCurrentActionData(response);
        }
      },
      onError: (error) => {
        console.error("Error updating binary action:", error);
      },
    },
  );

  const updateBinaryActionForActionMutation = useMutationData(
    "/binary_action_for_action",
    { method: "PUT" },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error updating binary action:", error);
      },
    },
  );

  // Mutation for uploading fulfillment file
  const uploadFulfillmentFileMutation = useMutationData(
    "/binary_verification_upload_for_followup",
    { method: "POST" },
    {
      onSuccess: () => {
        // Success will be handled by the calling function
      },
      onError: (error) => {
        console.error("Error uploading fulfillment file:", error);
      },
    },
  );

  const deCompleteFulfillmentMutation = useMutationData(
    "/binary_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: PROGRESS_CLARITY_STATUS.CHECK_FULFILLMENT,
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_CLARITY_STATUS.FULFILLMENT);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  const deCompleteVerificationMutation = useMutationData(
    "/binary_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: PROGRESS_CLARITY_STATUS.DONE,
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_CLARITY_STATUS.CHECK_FULFILLMENT);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  const rejectReqFulfillmentMutation = useMutationData(
    "/binary_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: PROGRESS_CLARITY_STATUS.FULFILLMENT,
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_CLARITY_STATUS.REVIEW);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  const completeCheckFulfillmentMutation = useMutationData(
    "/binary_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_CLARITY_STATUS.REVIEW);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  const updateVerificationStatusToReVerificationMutation = useMutationData(
    "/binary_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_CLARITY_STATUS.REVERIFICATION);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  const updateBinaryVerificationMutation = useMutationData(
    "/binary_verification",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        setVerificationStatus(response.verificationStatusCd);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  // New mutation for updating binary scan components
  const updateBinaryScanComponentsMutation = useMutationData(
    "/binary_scan_components",
    {
      method: "PUT",
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error updating binary scan components:", error);
      },
    },
  );

  const reUploadVerificationFileMutation = useMutationData(
    queryKeys.binaryVerificationUpload,
    {
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("common.re-upload-file-success"));
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
      },
      onError: (error) => {
        let errorMessage;
        // Only show error message becase customer's api upload
        if (error?.response?.data && (error?.response?.data?.code === "20300") && error?.response?.data?.additionalMessage) {
          const { additionalMessage } = error.response.data;
          errorMessage = additionalMessage;
        } else {
          // Fallback to extractErrorCode if no response data
          const errorCode = extractErrorCode(error, "common.error-occurred");
          errorMessage = t(errorCode);
        }

        useSuccessToast(TOAST.error, t("common.error"), errorMessage);
      },
    },
  );

  const addVerificationReportMutation = useMutationData(
    "/verification_report",
    { method: "POST" },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("common.verification-report-added-success"));
        handleGetVerificationReportForPopup();
      },
      onError: (error) => {
        console.error("Error adding verification report:", error);
      },
    },
  );

  const uploadBinaryVerificationFileMutation = useMutationData(
    "/binary_verification_upload_for_verification",
    {
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error during upload data:", error);
      },
    },
  );

  // Update project data when fetched
  useEffect(() => {
    if (fetchedProjectData) {
      setProjectData(fetchedProjectData);
    }
  }, [fetchedProjectData]);

  // Update verification data when fetched
  useEffect(() => {
    if (fetchedVerificationData) {
      setVerificationData(fetchedVerificationData);
      setVerificationStatus(fetchedVerificationData.verificationStatusCd);
      setApprovalFlag(fetchedVerificationData.approvalFlag);
      setApprovalRequestFlag(fetchedVerificationData.approvalRequestFlag);
      setVerificationComment(fetchedVerificationData.verificationComment);
      setVerificationFile(fetchedVerificationData.verificationRealFileName);
      // Initialize approval comment from fetched data
      setApprovalComment(fetchedVerificationData.approvalComment || "");
      setFulfillmentComment(fetchedVerificationData.followupComment || "");
    }
  }, [fetchedVerificationData]);

  const handleUpdateStatus = (newApprovalFlag, newApprovalRequestFlag, newStatus) => {
    if (newApprovalFlag === "N" && newApprovalRequestFlag === "Y") {
      // Call the API to update the approval request flag
      updateApprovalRequestFlagMutation.mutate({
        approvalRequestFlag: "Y",
        binaryVerificationSeq: binaryVerificationSeq,
      });
    } else if (newApprovalFlag === "Y" && newApprovalRequestFlag === "Y") {
      // Call the API to update the approval flag
      updateApprovalFlagMutation.mutate({
        approvalFlag: "Y",
        binaryVerificationSeq: binaryVerificationSeq,
        approvalComment: approvalComment, // Use actual approval comment instead of empty string
      });
    }

    // Still update the local state
    setApprovalFlag(newApprovalFlag);
    setApprovalRequestFlag(newApprovalRequestFlag);
    if (newStatus) {
      setVerificationStatus(newStatus);
    }
  };

  const handleMoveToReview = () => {
    let componentDataToSend = modifiedComponentData;
    if (!modifiedComponentData || modifiedComponentData.length === 0) {
      componentDataToSend = processComponentConflictFlags(fetchedBinaryScanComponent || [], fetchedLicensePolicy);
    } else {
      componentDataToSend = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    }
    return updateVerificationStatusToReviewMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.REVIEW,
      binaryScanComponents: componentDataToSend,
      binaryScanFollowupResults: [],
      verificationComment: verificationComment,
    });
  };

  // New function to handle rejection of verification requests
  const handleRejectVerification = () => {
    rejectVerificationRequestMutation.mutate({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.VERIFICATION,
    });
  };

  // New function to handle decompleting from DONE to VERIFICATION
  const handleDecompleteVerification = () => {
    decompleteVerificationMutation.mutate({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.CHECK_FULFILLMENT,
    });
  };

  const handleMoveToDone = () => {
    return forceToDoneMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.DONE,
      verificationComment: verificationComment,
      binaryScanFollowupResults: fulfillmentInfoRef.current ? fulfillmentInfoRef.current.getFulfillmentData() : binaryScanFollowupResults,
    });
  };

  const handleMoveToFulfillment = async () => {
    // Get the latest fulfillment data from the component if ref is available
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : binaryScanFollowupResults;

    // Process component data with conflict flags
    let componentDataToSend = modifiedComponentData;
    if (!modifiedComponentData || modifiedComponentData.length === 0) {
      componentDataToSend = processComponentConflictFlags(fetchedBinaryScanComponent || [], fetchedLicensePolicy);
    } else {
      componentDataToSend = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    }

    await moveToFulfillmentMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      binaryScanFollowupResults: updatedFollowupResults,
    });
    await updateBinaryVerificationToFulfillmentMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.FULFILLMENT,
      binaryScanFollowupResults: updatedFollowupResults,
      binaryScanComponents: componentDataToSend,
      verificationComment: verificationComment,
    });
    navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
  };

  const handleUpdateBinaryAction = (actionData) => {
    const { completionRequestDt, actionsContent, actionVerificationSeq } = actionData;
    let formattedDate = completionRequestDt || "";
    if (formattedDate && /^\d{4}-\d{2}-\d{2}$/.test(formattedDate)) {
      formattedDate = `${formattedDate}T00:00:00.000Z`;
    }
    const payload = {
      actionsContent: actionsContent || "",
      completionRequestDt: formattedDate,
      binaryVerificationSeq: actionVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.DONE,
      approvedFlag: "N",
      actedFlag: "N",
    };
    updateBinaryActionMutation.mutate(payload);
  };

  const handleUpdateBinaryActionForAction = (actionData) => {
    const { actionSeq, actionsResultContent } = actionData;
    const { actionSeq: currentActionSeq } = currentActionData;
    const payload = {
      actionSeq: currentActionSeq || actionSeq,
      actionsResultContent: actionsResultContent || "",
      actedFlag: "Y",
    };
    updateBinaryActionForActionMutation.mutate(payload);
  };

  const handleGetVerificationReportForPopup = () => {
    fetchedVerificationReportForPopup.mutate({
      projectId: projectId,
      verificationToolCd: VERIFICATION_TYPE.CLARITY,
      verificationVersion: verificationVersion,
      sortColumnName: "created_at",
      sortOrder: "desc",
    });
  };

  // Handler for forcing fulfillment completion
  const handleForceCompleteFulfillment = () => {
    forceToDoneMutation.mutate({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.DONE,
      verificationComment: verificationComment,
    });
  };

  // Handler for adding verification report
  const handleAddVerificationReport = (reportData) => {
    addVerificationReportMutation.mutate(reportData);
  };

  const handleToCompleteFulfillment = async () => {
    // Validate that we have the required data
    if (!binaryVerificationSeq) {
      console.error("Binary verification sequence is required");
      alert("Error: Missing verification sequence. Please refresh the page and try again.");
      return;
    }

    // Get the latest fulfillment data from the component if ref is available
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : binaryScanFollowupResults;

    // Get fulfillment comment from the component
    const fulfillmentComment = fulfillmentInfoRef.current ? fulfillmentInfoRef.current.getFulfillmentComment() : "";

    // Get form data for file upload
    const formData = fulfillmentInfoRef.current ? fulfillmentInfoRef.current.getFormDataForUpload() : new FormData();

    // Add additional required fields to form data
    formData.append("binaryVerificationSeq", binaryVerificationSeq);

    // Process component data with conflict flags
    let componentDataToSend = modifiedComponentData;
    if (!modifiedComponentData || modifiedComponentData.length === 0) {
      componentDataToSend = processComponentConflictFlags(fetchedBinaryScanComponent || [], fetchedLicensePolicy);
    } else {
      componentDataToSend = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    }

    try {
      // API Call 1: Update followup results
      await moveToFulfillmentMutation.mutateAsync({
        binaryVerificationSeq: binaryVerificationSeq,
        binaryScanFollowupResults: updatedFollowupResults,
      });

      // API Call 2: Update verification status to CHECK_FULFILLMENT
      await updateBinaryVerificationToFulfillmentMutation.mutateAsync({
        binaryVerificationSeq: binaryVerificationSeq,
        verificationStatusCd: PROGRESS_CLARITY_STATUS.CHECK_FULFILLMENT,
        binaryScanFollowupResults: updatedFollowupResults,
        binaryScanComponents: componentDataToSend,
        followupComment: fulfillmentComment,
      });

      // API Call 3: Upload fulfillment file
      try {
        await uploadFulfillmentFileMutation.mutateAsync(formData);
      } catch (uploadError) {
        console.error("Step 3/3 failed: Error uploading fulfillment file:", uploadError);
        // Even if file upload fails, the status was already updated
        alert("Fulfillment completed but file upload failed. You may need to upload the file manually.");
      }

      // All APIs completed successfully
      setVerificationStatus(PROGRESS_CLARITY_STATUS.CHECK_FULFILLMENT);
      navigate(`/projects/${projectSeq}`, { state: { activeTab: "clarity" } });
    } catch (error) {
      console.error("Error in handleToCompleteFulfillment:", error);
      alert("Failed to complete fulfillment. Please check your connection and try again.");
      throw error;
    }
  };

  const handleToDecompleteFulfillment = () => {
    deCompleteFulfillmentMutation.mutate({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.FULFILLMENT,
    });
  };

  const handleToRejectReqFulfillment = () => {
    rejectReqFulfillmentMutation.mutate({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.REVIEW,
    });
  };

  const handleToCompleteCheckFulfillment = async () => {
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : binaryScanFollowupResults;

    // Execute mutations sequentially
    await completeCheckFulfillmentMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.DONE,
      verificationComment: verificationComment,
    });
    await moveToFulfillmentMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      binaryScanFollowupResults: updatedFollowupResults,
    });
  };

  const handleToRequestReVerification = (dueDate) => {
    return updateVerificationStatusToReVerificationMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.REVERIFICATION,
      reverificationDueDt: dueDate ? dueDate.toISOString() : null,
    });
  };

  const handleReUploadFile = (requestData) => {
    const { requestComment, verifFile, scanTypeCd } = requestData;
    const formData = new FormData();
    formData.append("binaryFile", verifFile[0]);
    formData.append("binaryVerificationSeq", binaryVerificationSeq);
    reUploadVerificationFileMutation.mutate(formData);
    updateBinaryVerificationMutation.mutate({
      binaryVerificationSeq,
      requestComment,
      scanTypeCd,
      projectSeq,
      projectId,
      verificationVersion,
    });
  };

  const handleRejectRequestVerification = () => {
    rejectVerificationRequestMutation.mutate({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.VERIFICATION,
      approvalRequestFlag: "N",
      approvalFlag: "N",
    });
  };

  const handleRejectReview = () => {
    return rejectVerificationRequestMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.VERIFICATION,
      approvalRequestFlag: "Y",
      approvalFlag: "Y",
    });
  };

  const handleRejectFollowUp = () => {
    return rejectReqFulfillmentMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.REVIEW,
    });
  };

  const handleRejectCheckFollowUp = () => {
    return deCompleteFulfillmentMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.FULFILLMENT,
    });
  };

  const handleDeCompleteVerification = () => {
    return deCompleteVerificationMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.CHECK_FULFILLMENT,
    });
  };

  const handleMoveToUsageTypeCheck = () => {
    return updateApprovalRequestFlagMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.VERIFICATION,
      approvalRequestFlag: "Y",
      projectSeq: projectSeq,
    });
  };

  const handleMoveToUsageTypeInput = () => {
    return updateApprovalFlagMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.VERIFICATION,
      approvalFlag: "Y",
      projectSeq: projectSeq,
    });
  };

  const handleSaveBinaryScanComponents = () => {
    updateBinaryScanComponentsMutation.mutate(modifiedComponentData);
  };

  const handleRejectUsageTypeCheck = () => {
    return updateApprovalRequestFlagMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.VERIFICATION,
      approvalRequestFlag: "N",
    });
  };

  const handleRejectUseTypeInput = () => {
    return updateApprovalFlagMutation.mutateAsync({
      binaryVerificationSeq: binaryVerificationSeq,
      verificationStatusCd: PROGRESS_CLARITY_STATUS.VERIFICATION,
      approvalFlag: "N",
    });
  };

  // Computed properties
  const isReviewOrDoneStage =
    verificationStatus === PROGRESS_CLARITY_STATUS.REVIEW || verificationStatus === PROGRESS_CLARITY_STATUS.DONE;

  const isNotAnalysis = verificationStatus !== PROGRESS_CLARITY_STATUS.ANALYSIS;
  const isDone = verificationStatus === PROGRESS_CLARITY_STATUS.DONE;
  const isFulfillmentOrCheckFulfillmentStage =
    verificationStatus === PROGRESS_CLARITY_STATUS.FULFILLMENT ||
    verificationStatus === PROGRESS_CLARITY_STATUS.CHECK_FULFILLMENT;

  const isCheckFulfillmentStage = verificationStatus === PROGRESS_CLARITY_STATUS.CHECK_FULFILLMENT;

  // Combined loading state for all essential API calls
  // Check both isLoading (no data in cache) and isFetching (refetching data)
  const isLoading = 
    projectDataLoading ||
    verificationDataLoading ||
    licensePolicyLoading ||
    binaryScanComponentLoading;

  const isFetching = 
    projectDataFetching ||
    verificationDataFetching ||
    licensePolicyFetching ||
    binaryScanComponentFetching;

  return {
    // Loading state
    isLoading,
    isFetching,
    
    verificationStatus,
    approvalFlag,
    approvalRequestFlag,
    projectData,
    verificationData,
    fetchedLicensePolicy,
    fetchedBinaryScanComponent,
    fetchedBinaryScanFollowupPlan,
    binaryScanFollowupPlanUpdatedAt,
    fetchedBinaryAction,
    projectId,
    verificationVersion,
    handleUpdateStatus,
    handleMoveToReview,
    handleRejectVerification,
    handleDecompleteVerification,
    handleMoveToDone,
    handleUpdateBinaryActionForAction,
    handleUpdateBinaryAction,
    isReviewOrDoneStage,
    isNotAnalysis,
    isDone,
    updateApprovalFlagMutation,
    handleMoveToFulfillment,
    fulfillmentInfoRef, // Export the ref
    handleGetVerificationReportForPopup,
    verificationReportsData,
    handleForceCompleteFulfillment,
    handleAddVerificationReport,
    isFulfillmentOrCheckFulfillmentStage,
    isCheckFulfillmentStage,
    handleToCompleteFulfillment,
    handleToDecompleteFulfillment,
    handleToRejectReqFulfillment,
    handleToCompleteCheckFulfillment,
    handleToRequestReVerification,
    handleReUploadFile,
    verificationComment,
    setVerificationComment,
    verificationFile,
    setVerificationFile,
    // Export approval comment state
    approvalComment,
    setApprovalComment,
    fulfillmentComment,
    setFulfillmentComment,
    handleRejectRequestVerification,
    handleRejectReview,
    handleRejectFollowUp,
    handleRejectCheckFollowUp,
    handleDeCompleteVerification,
    handleMoveToUsageTypeCheck,
    handleMoveToUsageTypeInput,
    handleSaveBinaryScanComponents,
    handleRejectUsageTypeCheck,
    handleRejectUseTypeInput,
  };
};

export default useClarityVerification;
